import { useEffect, useMemo, useState, useCallback } from "react";
import {
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowUpTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { getMembers } from "../../../api/productApi";
import SearchableMemberSelect from "../../common/SearchableMemberSelect";
import useDamageForm from "../../../hooks/useDamageForm";
import BookHoldSection from "./BookHoldSection";

export default function AddDamage({ book, onClose, initialMemberId, initialOrderId }) {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token");

  /* ------------------ CORE DATA ------------------ */
  const [members, setMembers] = useState([]);

  /* ------------------ PAGINATION STATE ------------------ */
  const [currentPage, setCurrentPage] = useState(1);

  /* ------------------ HOOK INTEGRATION ------------------ */
  const {
    memberId,
    setMemberId,
    orderId,
    setOrderId,
    reason: type,
    setReason: setType,
    severity,
    setSeverity,
    copyId,
    setCopyId,
    date,
    setDate,
    amount: amt,
    setAmount: setAmt,
    remark,
    setRemark,
    sendEmail,
    setSendEmail,
    imagesBase64,
    orderIds,
    damageHistory: damageList,
    ordersLoading,
    copiesLoading,
    historyLoading,
    saving: submitting,
    error: err,
    successMsg: msg,
    copyOptions,
    handleImageUpload,
    handleSaveDamage: handleSubmit,
  } = useDamageForm({
    book,
    token,
    initialMemberId,
    initialOrderId,
  });

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => {
      setToast(null);
    }, 3000);
  }, []);

  /* ------------------ DROPDOWNS ------------------ */
  const reasonOptions = useMemo(() => [
    { label: "Select Dropdown", value: "" },
    { label: "Ripped Pages", value: "Ripped Pages" },
    { label: "Liquid Damage", value: "Liquid Damage" },
    { label: "Book Lost", value: "Book Lost" },
    { label: "Other Damages", value: "Other Damages" },
  ], []);

  const severityOptions = useMemo(() => [
    { label: "Select Dropdown", value: "" },
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
  ], []);

  /* ------------------ LOAD MEMBERS ------------------ */
  useEffect(() => {
    if (!token) return;
    let ignore = false;
    async function loadMembers() {
      try {
        const res = await getMembers({ token });
        if (!ignore) {
          const list = Array.isArray(res) ? res : (res?.data || []);
          const mapped = list.map(m => ({
            id: m.id,
            name: m.member_name || m.name,
            memberId: m.id,
            email: m.email || "",
            phone: m.phone || ""
          }));
          setMembers(mapped);
        }
      } catch (e) {
        console.error("Members API error", e);
      }
    }
    loadMembers();
    return () => { ignore = true; };
  }, [token]);

  /* ------------------ PAGINATION HELP ------------------ */
  const paginatedDamageList = useMemo(() => {
    const start = (currentPage - 1) * 5;
    return damageList.slice(start, start + 5);
  }, [damageList, currentPage]);

  const totalPages = Math.ceil(damageList.length / 5);

  return (
    <div className="flex flex-col gap-8 p-4 bg-gray-50/50 min-h-screen">
      {/* 1. SELECTION HEADER CARD */}
      <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex gap-4 items-center">
            <img
              src={book?.img || "https://via.placeholder.com/120x180"}
              alt={book?.title}
              className="w-16 h-24 object-cover rounded shadow-sm"
            />
            <div>
              <h3 className="font-bold text-lg text-gray-900 leading-tight">
                {book?.title || "Add Damage & Hold"}
              </h3>
              <p className="text-sm font-semibold text-blue-600 mt-1">
                {book?.credits ? `${book.credits} Credits` : "0 Credits"}
              </p>
              <p className="text-xs text-gray-400 mt-1">ID: {book?.id}</p>
            </div>
          </div>

          <div className="w-full md:w-80">
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Select Member:
            </label>
            <SearchableMemberSelect
              members={members}
              value={memberId}
              onChange={(id, memberObj) => {
                setMemberId(id);
                if (memberObj && !members.some(x => String(x.id) === String(id))) {
                  setMembers(prev => [...prev, memberObj]);
                }
              }}
              placeholder="Select Member"
            />
          </div>
        </div>
      </div>

      {/* 2. DAMAGE FORM CARD */}
      <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
        <div className="flex items-center gap-2 mb-6 border-b border-gray-100 pb-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-gray-900" />
          <h3 className="font-bold text-lg text-gray-900">Damage Book</h3>
        </div>

        {/* ERROR / SUCCESS MSGS */}
        {err && (
          <p className="bg-red-50 text-red-600 border border-red-100 px-4 py-3 rounded-lg text-sm mb-6 flex items-center gap-2 font-medium">
            ⚠️ {err}
          </p>
        )}
        {msg && (
          <p className="bg-green-50 text-green-700 border border-green-100 px-4 py-3 rounded-lg text-sm mb-6 flex items-center gap-2 font-medium">
            ✅ {msg}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <FieldSelect
            label="Order ID:"
            value={orderId}
            onChange={setOrderId}
            options={[
              { label: "Select Order", value: "" },
              ...orderIds.map((id) => ({
                label: `Order #${id}`,
                value: id,
              })),
            ]}
            loading={ordersLoading}
          />
          <FieldSelect
            label="Reason:"
            value={type}
            onChange={setType}
            options={reasonOptions}
          />
          <FieldSelect
            label="Severity:"
            value={severity}
            onChange={setSeverity}
            options={severityOptions}
          />
          <FieldSelect
            label="Copy:"
            value={copyId}
            onChange={setCopyId}
            options={copyOptions}
            loading={copiesLoading}
          />
          <FieldInput
            label="Date"
            type="date"
            value={date}
            onChange={setDate}
          />
          <FieldInput
            label="Amount"
            type="number"
            placeholder="₹ Amount"
            value={amt}
            onChange={setAmt}
            readOnly={type !== "Other Damages"}
          />
          <FieldInput
            label="Remark"
            placeholder="Remarks"
            value={remark}
            onChange={setRemark}
          />
        </div>

        {/* EMAIL & UPLOAD ROW */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
            />
            Send E-Mail Notification
          </label>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg cursor-pointer text-sm bg-white hover:bg-gray-50 font-medium transition-colors">
              <ArrowUpTrayIcon className="h-4 w-4 text-gray-700" />
              Upload Images
              <input
                type="file"
                multiple
                hidden
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files)}
              />
            </label>
            {imagesBase64.length > 0 && (
              <span className="text-xs text-green-600 font-semibold">
                {imagesBase64.length} image(s) selected
              </span>
            )}
          </div>
        </div>

        {/* FOOTER BUTTONS */}
        <div className="flex justify-between items-center border-t border-gray-100 pt-5">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-8 py-2.5 rounded-lg text-white font-semibold text-sm shadow-sm transition-colors ${submitting ? "bg-gray-400 cursor-not-allowed" : "bg-black hover:bg-gray-800"}`}
          >
            {submitting ? "Saving..." : "Save Damage"}
          </button>

          <button
            onClick={onClose}
            className="border border-gray-300 text-gray-700 px-10 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-50 shadow-sm transition-colors bg-white"
          >
            Close
          </button>
        </div>
      </div>

      {/* 3. BOOK ON HOLD CARD */}
      <BookHoldSection
        book={book}
        token={token}
        copyOptions={copyOptions}
        copiesLoading={copiesLoading}
        showToast={showToast}
      />

      {/* 4. DAMAGE HISTORY CARD */}
      <div className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <ClockIcon className="h-5 w-5 text-gray-900" />
          <h3 className="font-bold text-lg text-gray-900">Damage History</h3>
        </div>
        <p className="text-gray-400 text-sm mb-6">
          Select an Order ID to view history.
        </p>

        {historyLoading ? (
          <div className="flex justify-center items-center py-10">
            <span className="text-gray-400 text-sm animate-pulse">Loading history...</span>
          </div>
        ) : damageList.length === 0 ? (
          <p className="text-gray-400 text-sm italic py-4 text-center border border-dashed border-gray-200 rounded-lg">
            {orderId
              ? "No damage found for this order."
              : "Select an Order ID to view history."}
          </p>
        ) : (
          <div>
            <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
              <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
                <thead className="bg-gray-50 text-gray-700 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5">Order ID</th>
                    <th className="px-6 py-3.5">Type</th>
                    <th className="px-6 py-3.5">Severity</th>
                    <th className="px-6 py-3.5">Amount</th>
                    <th className="px-6 py-3.5">Date</th>
                    <th className="px-6 py-3.5">Remark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-150 bg-white">
                  {paginatedDamageList.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-3 text-gray-900 font-medium">#{d.added_order_id}</td>
                      <td className="px-6 py-3 text-gray-600">{d.type || "—"}</td>
                      <td className="px-6 py-3 text-gray-600">{d.severity || "—"}</td>
                      <td className="px-6 py-3 text-gray-955 font-semibold">₹{d.amt}</td>
                      <td className="px-6 py-3 text-gray-550">{d.date || "—"}</td>
                      <td className="px-6 py-3 text-gray-650 truncate max-w-[200px]" title={d.remark}>
                        {d.remark || "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION CONTROL */}
            <div className="flex justify-center items-center gap-1.5 mt-6">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-white shadow-sm"
              >
                <ChevronLeftIcon className="h-4 w-4 text-gray-700" />
              </button>

              {Array.from({ length: totalPages || 1 }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-8 h-8 flex items-center justify-center text-xs font-semibold rounded-lg border transition-colors shadow-sm ${currentPage === p
                    ? "bg-black text-white border-black"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
                >
                  {p}
                </button>
              ))}

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages || 1))}
                disabled={currentPage === (totalPages || 1)}
                className="p-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-white shadow-sm"
              >
                <ChevronRightIcon className="h-4 w-4 text-gray-700" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TOAST NOTIFICATION */}
      {toast && (
        <div className={`fixed top-5 right-5 z-[9999] px-4 py-3 rounded-lg shadow-xl flex items-center gap-2 border text-sm font-semibold transition-all duration-300 animate-fade-in ${toast.type === "success"
          ? "bg-white text-green-700 border-green-200"
          : "bg-white text-red-600 border-red-200"}`}>
          <span>{toast.type === "success" ? "✅" : "⚠️"}</span>
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}

/* ------------------ HELPERS ------------------ */
function FieldSelect({ label, value, onChange, options, loading = false }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
        {label}
        {loading && <span className="text-[10px] text-gray-450 animate-pulse">Loading...</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-full outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 transition-colors shadow-sm text-gray-800"
      >
        {options.map((o, i) => (
          <option key={i} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function FieldInput({ label, value, onChange, type = "text", placeholder, readOnly = false }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-gray-700">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        readOnly={readOnly}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white w-full outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300 transition-colors shadow-sm text-gray-800 read-only:bg-gray-50 read-only:text-gray-400 read-only:cursor-not-allowed"
      />
    </div>
  );
}
